<template>
  <Modal
    ref="modal"
    class="supervision-mg-modal"
    :small="true"
    :close-button="true"
    @hidden="alCerrar"
  >
    <div v-if="datos" class="supervision-mg-confirmar">
      <div class="supervision-mg-confirmar__pasos">Paso {{ paso }} de 2</div>

      <h2 class="box__title supervision-mg-confirmar__titulo">
        <i class="iconoir-warning-triangle"></i>
        {{ datos.accion }}
      </h2>

      <template v-if="paso === 1">
        <p class="supervision-mg-confirmar__texto">
          Revisa bien a quién y qué vas a cambiar. Estás en este espacio como
          <b>supervisor</b>: no apareces en sus miembros, pero el cambio sí lo
          verán, y el registro de auditoría lo guarda con tu nombre.
        </p>

        <dl class="supervision-mg-confirmar__datos">
          <dt>Espacio de trabajo</dt>
          <dd>{{ datos.espacio }}</dd>
          <dt>Persona</dt>
          <dd>
            <template v-if="datos.persona.nombre">
              {{ datos.persona.nombre }} ·
            </template>
            <span class="supervision-mg-confirmar__correo">{{
              datos.persona.correo
            }}</span>
          </dd>
          <dt>Qué pasa</dt>
          <dd class="supervision-mg-confirmar__cambio">{{ datos.cambio }}</dd>
        </dl>

        <div class="supervision-mg-confirmar__botones">
          <Button ref="cancelar" type="secondary" @click="cancelar">
            Cancelar
          </Button>
          <Button type="danger" :disabled="espera > 0" @click="paso = 2">
            {{ espera > 0 ? `Lee con calma… ${espera}` : 'Sí, continuar' }}
          </Button>
        </div>
      </template>

      <template v-else>
        <p class="supervision-mg-confirmar__texto">
          Para confirmar, escribe el correo de la persona:
          <code class="supervision-mg-confirmar__frase">{{ datos.frase }}</code>
        </p>
        <FormInput
          ref="entrada"
          v-model="escrito"
          class="supervision-mg-confirmar__entrada"
          placeholder="correo de la persona"
          autocomplete="off"
          @paste.prevent
          @drop.prevent
          @keydown.enter.prevent="confirmar"
        />
        <p class="supervision-mg-confirmar__ayuda">
          Hay que escribirlo: pegarlo no funciona, a propósito.
        </p>
        <div class="supervision-mg-confirmar__botones">
          <Button type="secondary" @click="cancelar">Cancelar</Button>
          <Button type="danger" :disabled="!coincide" @click="confirmar">
            Confirmar cambio
          </Button>
        </div>
      </template>
    </div>
  </Modal>
</template>

<script>
import Button from '@baserow/modules/core/components/Button.vue'
import { supervision } from '@extras_mg/supervision'

const SEGUNDOS = 3

/**
 * La confirmacion de los supervisores (Victor e Isaac) al cambiar un rol o
 * quitar a alguien en un espacio que supervisan. La pide el SERVIDOR (ver
 * extras_mg/supervision/confirmacion.py); esto solo la ensena.
 *
 * Friccion a proposito, para que un clic de mas no le cambie el rol a nadie:
 *   1. Ver el resumen (espacio, persona, que pasa) con el boton de seguir
 *      apagado unos segundos. El foco empieza en Cancelar.
 *   2. Escribir el correo de la persona, sin poder pegarlo.
 * Cerrar el modal de cualquier forma (X, Esc, clic fuera) es cancelar.
 */
export default {
  name: 'ConfirmacionSupervision',
  components: { Button },
  data() {
    return {
      paso: 1,
      escrito: '',
      espera: SEGUNDOS,
      reloj: null,
      resuelto: false,
    }
  },
  computed: {
    pedido() {
      return supervision.pedido
    },
    datos() {
      return this.pedido?.datos || null
    },
    coincide() {
      return (
        this.datos &&
        this.escrito.trim().toLowerCase() === this.datos.frase.toLowerCase()
      )
    },
  },
  watch: {
    pedido(nuevo) {
      if (nuevo) {
        this.empezar()
      }
    },
    paso(nuevo) {
      if (nuevo === 2) {
        this.$nextTick(() => this.$refs.entrada?.focus?.())
      }
    },
  },
  beforeUnmount() {
    clearInterval(this.reloj)
  },
  methods: {
    empezar() {
      this.paso = 1
      this.escrito = ''
      this.espera = SEGUNDOS
      this.resuelto = false
      clearInterval(this.reloj)
      this.reloj = setInterval(() => {
        this.espera = Math.max(0, this.espera - 1)
        if (this.espera === 0) {
          clearInterval(this.reloj)
        }
      }, 1000)
      this.$refs.modal.show()
      this.$nextTick(() => this.$refs.cancelar?.$el?.focus?.())
    },
    responder(frase) {
      if (this.resuelto) {
        return
      }
      this.resuelto = true
      clearInterval(this.reloj)
      this.pedido?.resolver(frase)
    },
    confirmar() {
      if (!this.coincide) {
        return
      }
      const frase = this.escrito.trim()
      this.responder(frase)
      this.$refs.modal.hide()
    },
    cancelar() {
      this.responder(null)
      this.$refs.modal.hide()
    },
    alCerrar() {
      // Cerrar sin confirmar (X, Esc, clic fuera) es cancelar.
      this.responder(null)
    },
  },
}
</script>

<style lang="scss">
/* Sin colores fijos: build-theme.py invierte cualquier hex del CSS compilado. */
/* Encima de cualquier otro modal (los del core van en 7): la confirmacion
   puede salir desde uno abierto, como el de "Quitar del espacio de trabajo".
   Debajo de los avisos (10). */
.modal__wrapper.supervision-mg-modal {
  z-index: 8;
}

.supervision-mg-confirmar__pasos {
  font-size: 12px;
  opacity: 0.6;
  margin-bottom: 4px;
}

.supervision-mg-confirmar__titulo {
  display: flex;
  align-items: center;
  gap: 8px;
}

.supervision-mg-confirmar__texto {
  font-size: 13px;
  line-height: 1.5;
  margin: 0 0 16px;
}

.supervision-mg-confirmar__datos {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 8px 16px;
  font-size: 13px;
  margin: 0 0 20px;
  padding: 12px 16px;
  border-radius: 6px;
  background: color-mix(in srgb, currentColor 6%, transparent);
}

.supervision-mg-confirmar__datos dt {
  opacity: 0.6;
}

.supervision-mg-confirmar__datos dd {
  margin: 0;
  font-weight: 500;
}

.supervision-mg-confirmar__correo,
.supervision-mg-confirmar__frase {
  font-family: monospace;
}

.supervision-mg-confirmar__cambio {
  font-weight: 600;
}

.supervision-mg-confirmar__frase {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  background: color-mix(in srgb, currentColor 10%, transparent);
  user-select: all;
}

.supervision-mg-confirmar__entrada {
  width: 100%;
}

.supervision-mg-confirmar__ayuda {
  font-size: 12px;
  opacity: 0.6;
  margin: 6px 0 16px;
}

.supervision-mg-confirmar__botones {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
